import React, { useState, useEffect } from 'react';
import { DisplayModeWindow } from './DisplayModeWindow';
import { DisplayModeFullscreen } from './DisplayModeFullscreen';

/**
 * StreamingFeatureHost - Main component for hosting streamed features
 * 
 * Handles:
 * - Viewport detection (desktop vs mobile)
 * - Display mode selection (window vs fullscreen)
 * - Shadow DOM creation
 * - Bundle loading
 * - Mount/unmount lifecycle
 * 
 * @param {Object} props
 * @param {string} props.bundleUrl - Base URL for feature bundle
 * @param {string} props.title - Window title
 * @param {Function} props.onClose - Callback when feature is closed
 * @param {Object} props.windowProps - Additional props for DraggableWindow
 * @param {Object} props.position - Initial window position (desktop only)
 * @param {Object} props.size - Initial window size (desktop only)
 */
export function StreamingFeatureHost({
  bundleUrl,
  title = 'Streamed Feature',
  onClose,
  windowProps = {},
  position = { x: 100, y: 100 },
  size = { width: 1200, height: 800 }
}) {
  const [displayMode, setDisplayMode] = useState('window'); // 'window' | 'fullscreen'
  const DESKTOP_BREAKPOINT = 1024;

  // Detect viewport size and set display mode
  // If embedded in window system, use window mode (not fullscreen)
  // If forceFullscreen is set in windowProps, always use fullscreen
  useEffect(() => {
    const updateDisplayMode = () => {
      // Force fullscreen if specified
      if (windowProps.forceFullscreen) {
        setDisplayMode('fullscreen');
        return;
      }
      
      // If embedded in window system, always use window mode
      if (windowProps.embedded) {
        setDisplayMode('window');
        return;
      }
      
      const width = window.innerWidth;
      setDisplayMode(width >= DESKTOP_BREAKPOINT ? 'window' : 'fullscreen');
    };

    // Set initial mode
    updateDisplayMode();

    // Only listen for resize if not forcing fullscreen and not embedded
    if (!windowProps.forceFullscreen && !windowProps.embedded) {
      window.addEventListener('resize', updateDisplayMode);
      return () => window.removeEventListener('resize', updateDisplayMode);
    }
  }, [windowProps.forceFullscreen, windowProps.embedded]);

  if (!bundleUrl) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-red-400">Error: bundleUrl is required</div>
      </div>
    );
  }

  // Render appropriate display mode
  if (displayMode === 'fullscreen') {
    return (
      <DisplayModeFullscreen
        bundleUrl={bundleUrl}
        title={title}
        onClose={onClose}
        {...windowProps}
      />
    );
  }

  return (
    <DisplayModeWindow
      bundleUrl={bundleUrl}
      title={title}
      onClose={onClose}
      position={position}
      size={size}
      {...windowProps}
    />
  );
}

export default StreamingFeatureHost;

